import { connect } from 'pwa-helpers';
import { html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { StoreState } from '../../../store/store.interface';
import '../../pagination/in-search/pagination';
import '../../search-v2/search-v2-pagination';
import { TAB_NAMES } from '../constants';

/**
 * A component to display pagination messages for search results based on the active tab.
 */
@customElement('mhecomm-tabbed-search-pagination-message')
export class TabbedSearchPaginationMessage extends connect(store)(BaseComponent) {
  /**
   * The message template for when results are found.
   * Placeholders available: %first%, %last%, %total%, %term%, %business-unit%.
   */
  @property()
  message = 'Showing %first%-%last% of %total% results matching “%term%”';

  /**
   * The message displayed when no results are found.
   * Placeholders available: %term%.
   */
  @property()
  emptyMessage = '0 Results Matching "%term%"';

  /**
   * The message displayed when no search term is provided.
   * Placeholders available: %first%, %last%, %total%.
   */
  @property()
  noTermMessage = 'Showing %first%-%last% of %total% Results';

  /**
   * Stores the currently active search tab (e.g., Product, Support, Articles).
   */
  @state()
  activeTab = '';

  /**
   * Define styles for the component.
   */
  static styles = css`
    .default-height {
      min-height: 1.5rem;
    }
  `;

  /**
   * Renders the pagination message based on the active tab.
   */
  renderComp() {
    return html`<div class='default-height'>${this.renderPaginationComponents()}</div>`;
  }

  /**
   * Determines which pagination component to render based on the active tab.
   */
  renderPaginationComponents() {
    const tabContent = {
      Product: () => this.showSearchPaginationMsg(),
      Support: () => this.showSearchV2PaginationMsg(),
      Articles: () => this.showSearchPaginationMsg(),
    };
    return tabContent[this.activeTab]?.() || nothing;
  }

  /**
   * Updates the active tab when the Redux state changes.
   *
   * @param {StoreState} state - The updated state from the Redux store.
   */
  stateChanged(state: StoreState) {
    const newTab = state.tabbedSearchActiveTabState.activeTabKey;
    if (this.activeTab !== newTab) {
      this.activeTab = newTab;
      this.requestUpdate();
    }
  }

  /**
   * Gets the message with business unit appended for Product and Articles tabs.
   */
  getDisplayMessage() {
    return this.message + (this.activeTab === TAB_NAMES.PRODUCT || this.activeTab === TAB_NAMES.ARTICLES ? ' in %business-unit%' : '');
  }

  /**
   * Displays the search pagination message for the standard search results.
   */
  showSearchPaginationMsg() {
    return html`
      <mhecomm-pagination-message
        message="${this.getDisplayMessage()}"
        id="pagination_message"
        data-automation-id="pagination_message"
        .useAriaLive="${false}"
        aria-hidden="true"
      ></mhecomm-pagination-message>
    `;
  }

  /**
   * Displays the search pagination message for the V2 search results.
   */
  showSearchV2PaginationMsg() {
    return html`
      <mhecomm-search-v2-pagination-message
        message="${this.getDisplayMessage()}"
        id="pagination_message"
        data-automation-id="pagination_message"
        use-aria-live="true"
      ></mhecomm-search-v2-pagination-message>
    `;
  }
}

// Define the component in the global HTMLElement tag name map.
declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-tabbed-search-pagination-message': TabbedSearchPaginationMessage;
  }
}
